import React, { useContext, useRef } from "react";
import PropTypes from "prop-types";
import { Form, Formik } from "formik";
import TextBox from "../common/TextBox";
import Button from "../common/Button";
import OnboardContext from "./context";
import { langComponent } from "../../constants";
import AppContext from "../../AppContext";

import { ReactComponent as ArrowRightSvg } from "../../assets/icons/arrow-right.svg";
import { ReactComponent as ArrowLeftSvg } from "../../assets/icons/arrow-left.svg";
import { ReactComponent as VisaSvg } from "../../assets/payment/visa.svg";
import { ReactComponent as MasterCardSvg } from "../../assets/payment/mastercard.svg";
import { ReactComponent as UnionPaySvg } from "../../assets/payment/unionpay.svg";
import { ReactComponent as JcbSvg } from "../../assets/payment/jcb.svg";
import _ from "lodash";
import { format, startOfMonth } from "date-fns";
import ComboBox from "../common/ComboBox";

const monthOptions = _.range(1, 13).map((_, month) => ({
  name: format(new Date(1900, month, 1), "MMMM"),
  value: (month + 1).toString(),
}));

const Payment = () => {
  const { goBack, goForward, information, addInformation } =
    useContext(OnboardContext);
  const { getLang } = useContext(AppContext);
  const TranslationCommon = getLang(langComponent.common);
  const TranslationProfile = getLang(langComponent.profile);

  const handleValidate = ({ card, month, year, cvc, name }) => {
    let errors = {};

    if (!card) errors.card = TranslationCommon("Required");
    if (!month) errors.month = TranslationCommon("Required");

    if (!year) errors.year = TranslationCommon("Required");
    if (!cvc) errors.cvc = TranslationCommon("Required");
    if (!name) errors.name = TranslationCommon("Required");

    if (year && year.length != 2) errors.year = TranslationCommon("Required");
    if (cvc && cvc.length != 3) errors.cvc = TranslationCommon("Required");

    // * Check expiration date
    if (month && year) {
      const current = startOfMonth(new Date());
      const exp = startOfMonth(new Date(+`20${year}`, +month - 1, 1));

      if (exp < current) errors.year = TranslationCommon("Expired");
    }

    return errors;
  };

  const handleSubmit = (
    { card, month, year, cvc, name },
    { setSubmitting }
  ) => {
    setSubmitting(true);

    // * Create card token
    Omise.createToken(
      "card",
      {
        expiration_month: +month,
        expiration_year: +`20${year}`,
        number: card,
        security_code: cvc,
        name,
      },
      async (_statusCode, response) => {
        if (!response || !response.card) {
          toast.error("Unable to create card");
          setSubmitting(false);
          console.error(response);
          return;
        }

        const cardToken = response.id;

        addInformation({ payment: cardToken });
        goForward();
      }
    );
  };

  const _getCardType = (cardNumber) => {
    // Remove all non-digit characters from the input
    const sanitized = cardNumber.replace(/\D/g, "");

    // Regex patterns for various card types
    const cardPatterns = {
      visa: /^4\d{12}(\d{3})?$/,
      mastercard:
        /^(5[1-5]\d{4}|222[1-9]|22[3-9]\d{3}|2[3-6]\d{4}|27[01]\d{2}|2720)\d{10}$/,
      jcb: /^(?:2131|1800|35\d{3})\d{11}$/,
      unionpay: /^(62\d{14,17}|6[5-9]\d{13,18})$/,
    };

    // Check the card number against each pattern
    for (const [cardType, pattern] of Object.entries(cardPatterns)) {
      if (pattern.test(sanitized)) {
        return cardType;
      }
    }

    // If no pattern matches, return 'unknown'
    return "unknown";
  };

  return (
    <div>
      <h1 className="onboard__header">{TranslationProfile("Payment")}</h1>
      <Formik
        initialValues={
          information?.payment || {
            card: "4242424242424242",
            expiration: "",
            cvc: "123",
            name: "James Test",
            year: "26",
            month: "1",
          }
        }
        validate={handleValidate}
        onSubmit={handleSubmit}
      >
        {({ values }) => {
          const _cardType = _getCardType(values.card);

          const getCardTypeClassname = (type) => {
            if (_cardType === type) {
              return "cart-content-payment-form-card-types__icon cart-content-payment-form-card-types__icon--active";
            }

            return "cart-content-payment-form-card-types__icon";
          };

          return (
            <Form className="onboard__form onboard__form-payment">
              <div className="onboard__form-content">
                <div className="onboard__form-item">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      fontSize: 14,
                      color: "#333",
                      margin: "0 8px",
                    }}
                  >
                    Card no
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 4 }}
                    >
                      <VisaSvg
                        className={getCardTypeClassname("visa")}
                        height={20}
                        width={20}
                      />
                      <MasterCardSvg
                        className={getCardTypeClassname("mastercard")}
                        height={20}
                        width={20}
                      />
                      <JcbSvg
                        className={getCardTypeClassname("jcb")}
                        height={20}
                        width={20}
                      />
                      <UnionPaySvg
                        className={getCardTypeClassname("unionpay")}
                        height={20}
                        width={20}
                      />
                    </div>
                  </div>
                  <TextBox
                    name="card"
                    mask="9999 9999 9999 9999"
                    maskPlaceholder=""
                    placeholder="0000 0000 0000 0000"
                  />
                </div>
                <div className="onboard__form-item">
                  <div className="onboard__form-carddetail">
                    <ComboBox
                      name={"month"}
                      placeholder={"Month"}
                      options={monthOptions}
                      label={"Expiration"}
                    />
                    <TextBox
                      name="year"
                      placeholder={"YY"}
                      label={"Year"}
                      hideLimit
                      mask="99"
                      maskPlaceholder=""
                      autocomplete="off"
                    />
                    <TextBox
                      name="cvc"
                      placeholder={"***"}
                      label={"CVC"}
                      hideLimit
                      mask="999"
                      maskPlaceholder=""
                      autocomplete="off"
                    />
                  </div>
                </div>
                <div className="onboard__form-item">
                  <TextBox name="name" label={"Name on card"} />
                </div>
              </div>
              <div className="onboard__form-cta">
                <Button
                  label={TranslationCommon("Return")}
                  leadingIcon={<ArrowLeftSvg />}
                  onClick={goBack}
                  variant="secondary"
                />
                <div className="onboard__form-cta__divider" />
                <Button
                  type="submit"
                  label={TranslationCommon("Continue")}
                  trailingIcon={<ArrowRightSvg />}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

Payment.propTypes = {};

export default Payment;
